import { MultiSelect } from "@twinkltech/curriculum-architect-component-library";
import { Button, Text } from "@twinkltech/web-ui";
import { useTranslation } from "react-i18next";

import { useUrlFilters } from "@/hooks/useUrlFilters/useUrlFilters";

import { Z_INDEX_ORDERS } from "@/pages/config/zIndexOrders.const";
import { EDIT_SUBJECT_PLAN_NS } from "@/pages/EditSubjectPlan/EditSubjectPlan.i18n";

import { ResourceHelper } from "@/utils/ResourceHelper/ResourceHelper";

import { COMMON_NS } from "@/i18n/common/common.i18n";

import type {
  ExploreSubjectSchemeUnit,
  KeyStagesSlug,
  ThreadSlug,
} from "@twinkltech/curriculum-architect-domain-hooks";

export const SchemeUnitFilters = ({
  units,
}: {
  units: ExploreSubjectSchemeUnit[];
}) => {
  const { t } = useTranslation(EDIT_SUBJECT_PLAN_NS);
  const {
    clearAllFilters,
    filterOptions: filterValues,
    setFilterValue,
  } = useUrlFilters({ filterKeys: ["keyStages", "threads"] });

  const keyStageTags = new Set<KeyStagesSlug>(
    units.flatMap((unit) => unit.keyStagesSlug)
  );

  // Create options for key stages filter dropdown
  const keyStageOptions = Array.from(keyStageTags).map((keyStage) => ({
    value: keyStage,
    label: ResourceHelper.getKeyStageLabel(keyStage),
  }));

  const orderedKeyStageOptions = ResourceHelper.sortKeyStageOptions(keyStageOptions);

  const threadTagSet = new Set<ThreadSlug>(
    units.flatMap((unit) => unit.threadSlugs)
  );

  // Create options for threads filter dropdown
  const threadOptions = Array.from(threadTagSet).map((thread) => ({
    value: thread,
    label: t(`threads.${thread}`, { ns: COMMON_NS }),
  }));

  return (
    <div aria-labelledby="filter-units" className="flex mb-9" role="search">
      <div className="w-full flex gap-4 items-center grow">
        <Text
          as="span"
          className="sr-only"
          id="filter-units"
          variant="lg-medium"
        >
          {t("filter-units.title")}
        </Text>
        <div className="flex gap-3">
          {orderedKeyStageOptions.length > 0 && (
            <MultiSelect
              className={"w-[180px]"}
              label="Age Groups"
              options={orderedKeyStageOptions ?? []}
              value={filterValues.keyStages || []}
              zindex={Z_INDEX_ORDERS.EDIT_SUBJECT_PLAN.FILTER_DROPDOWN}
              onChange={(value: string[]) => {
                setFilterValue("keyStages", value);
              }}
            />
          )}
          {threadOptions.length > 0 && (
            <MultiSelect
              className={"w-[180px]"}
              label="Threads"
              options={threadOptions ?? []}
              value={filterValues.threads || []}
              zindex={Z_INDEX_ORDERS.EDIT_SUBJECT_PLAN.FILTER_DROPDOWN}
              onChange={(value: string[]) => {
                setFilterValue("threads", value);
              }}
            />
          )}
        </div>
        <Button
          as="button"
          className="text-[#1C7AC7] underline"
          size="sm"
          type="button"
          variant="link"
          onClick={clearAllFilters}
        >
          <Text variant="sm-normal">
            {t("actions.clear-filters", { ns: COMMON_NS })}
          </Text>
        </Button>
      </div>
    </div>
  );
};
